import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  ArrowLeft, ArrowRight, Check, Plus, X, Building2,
  Briefcase, Award, Loader2, Search, Upload, Package, Sparkles
} from "lucide-react";
import AIProfileBuilder from "@/components/AIProfileBuilder";
import { ProfileEditHints } from "@/components/OnboardingHints";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WIZARD_STEPS = [
  { id: 1, title: "Company", icon: Building2, description: "Basic info" },
  { id: 2, title: "Services", icon: Briefcase, description: "What you offer" },
  { id: 3, title: "Offerings", icon: Package, description: "Packages & classes" },
  { id: 4, title: "Credentials", icon: Award, description: "Awards & social" },
  { id: 5, title: "Review", icon: Check, description: "Final check" },
];

// Reusable SearchableSelect
function SearchableSelect({ label, options, value, onChange, placeholder, allowAdd = false, taxonomyType, required = false }) {
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const wrapperRef = useRef(null);

  const filteredOptions = options.filter(opt => opt.toLowerCase().includes(search.toLowerCase()));

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) setIsOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleAddNew = async () => {
    if (!search.trim()) return;
    setAdding(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/taxonomy/add`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ type: taxonomyType, value: search.trim() })
      });
      const data = await response.json();
      if (response.ok) {
        onChange(search.trim());
        toast.success(`Added "${search.trim()}"`);
        setIsOpen(false);
        setSearch("");
      } else {
        toast.error(data.detail || "Failed to add");
      }
    } catch (error) {
      toast.error("Failed to add");
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="space-y-1.5" ref={wrapperRef}>
      <Label>{label} {required && <span className="text-red-500">*</span>}</Label>
      <div className="relative">
        <div className="flex items-center border rounded-lg px-3 py-2.5 cursor-pointer bg-white hover:border-blue-300" onClick={() => setIsOpen(!isOpen)}>
          <Search className="w-4 h-4 text-gray-400 mr-2" />
          <input type="text" value={isOpen ? search : value}
            onChange={(e) => { setSearch(e.target.value); if (!isOpen) setIsOpen(true); }}
            placeholder={placeholder} className="flex-1 outline-none bg-transparent text-sm" onFocus={() => setIsOpen(true)} />
          {value && !isOpen && <X className="w-4 h-4 text-gray-400 cursor-pointer" onClick={(e) => { e.stopPropagation(); onChange(""); }} />}
        </div>
        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
            {filteredOptions.length > 0 ? filteredOptions.map((opt, i) => (
              <div key={i} className={`px-3 py-2 cursor-pointer hover:bg-blue-50 text-sm ${opt === value ? "bg-blue-100" : ""}`}
                onClick={() => { onChange(opt); setIsOpen(false); setSearch(""); }}>
                {opt}
              </div>
            )) : <div className="px-3 py-2 text-sm text-gray-500">No matches</div>}
            {allowAdd && search.trim() && !options.includes(search.trim()) && (
              <div className="px-3 py-2 cursor-pointer hover:bg-green-50 text-sm text-green-600 border-t flex items-center gap-2" onClick={handleAddNew}>
                {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add "{search.trim()}"
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function EnterpriseWizard() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [taxonomy, setTaxonomy] = useState({ company_types: [], cities: [] });
  const [uploading, setUploading] = useState(false);
  const [showAIBuilder, setShowAIBuilder] = useState(false);
  const logoInputRef = useRef(null);

  const [formData, setFormData] = useState({
    company_name: "", tagline: "", company_type: "", city: "",
    founded_year: "", team_size: "", years_in_business: "",
    location: "", branches: [],  // HQ Location and branch offices
    about: "", services: [],
    logo: "",
    offerings: [],  // {title, description, duration, price, mode}
    awards: [], clients: [],
    social_links: [], website: "",
    contact_email: "", contact_phone: "",  // Contact info
    terms_accepted: false
  });

  const [newOffering, setNewOffering] = useState({ title: "", description: "", duration: "", price: "", mode: "offline" });

  useEffect(() => {
    fetchTaxonomy();
    fetchExistingProfile();
  }, []);

  const fetchTaxonomy = async () => {
    try {
      const response = await fetch(`${API_URL}/api/taxonomy`);
      if (response.ok) {
        const data = await response.json();
        const grouped = data.grouped || {};
        setTaxonomy({
          company_types: (grouped.company_type || []).map(c => c.value),
          cities: (grouped.city || []).map(c => c.value)
        });
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const fetchExistingProfile = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/profiles/me`, { headers: { Authorization: `Bearer ${token}` } });
      if (response.ok) {
        const data = await response.json();
        if (data?.profile_id) {
          // Convert social_presence individual fields to array format for form
          const socialLinks = [];
          const sp = data.social_presence || {};
          if (sp.instagram) socialLinks.push({ platform: "Instagram", url: sp.instagram });
          if (sp.facebook) socialLinks.push({ platform: "Facebook", url: sp.facebook });
          if (sp.facebook_page) socialLinks.push({ platform: "Facebook Page", url: sp.facebook_page });
          if (sp.linkedin) socialLinks.push({ platform: "LinkedIn", url: sp.linkedin });
          if (sp.youtube) socialLinks.push({ platform: "YouTube", url: sp.youtube });
          if (sp.twitter) socialLinks.push({ platform: "Twitter/X", url: sp.twitter });
          if (sp.personal_website) socialLinks.push({ platform: "Website", url: sp.personal_website });
          
          setFormData(prev => ({
            ...prev,
            company_name: data.personal_info?.full_name || "",
            tagline: data.professional_info?.headline || "",
            company_type: data.professional_info?.primary_category || "",
            city: data.personal_info?.city || "",
            founded_year: data.enterprise_info?.founded_year || "",
            team_size: data.enterprise_info?.team_size || "",
            years_in_business: data.enterprise_info?.years_in_business || "",
            location: data.enterprise_info?.location || "",
            branches: data.enterprise_info?.branches || [],
            about: data.professional_info?.bio || "",
            services: data.enterprise_info?.services || [],
            logo: data.portfolio?.profile_photo || "",
            offerings: data.enterprise_info?.offerings || [],
            awards: data.professional_info?.awards || [],
            clients: data.enterprise_info?.notable_clients || [],
            social_links: socialLinks,
            contact_email: data.contact_management?.contact_email || "",
            contact_phone: data.contact_management?.contact_phone || "",
            terms_accepted: data.preferences?.terms_accepted || false
          }));
        }
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const updateField = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const saveDraft = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const payload = {
        personal_info: {
          full_name: formData.company_name,
          display_name: formData.company_name.split(" ")[0],
          city: formData.city
        },
        professional_info: {
          headline: formData.tagline,
          primary_category: formData.company_type,
          bio: formData.about,
          awards: formData.awards
        },
        portfolio: { profile_photo: formData.logo },
        enterprise_info: {
          founded_year: formData.founded_year,
          team_size: formData.team_size,
          years_in_business: formData.years_in_business ? parseInt(formData.years_in_business) : null,
          services: formData.services,
          notable_clients: formData.clients,
          location: formData.location,
          branches: formData.branches,
          offerings: formData.offerings
        },
        social_presence: {
          instagram: formData.social_links.find(l => l.platform === "Instagram")?.url || null,
          facebook: formData.social_links.find(l => l.platform === "Facebook")?.url || null,
          facebook_page: formData.social_links.find(l => l.platform === "Facebook Page")?.url || null,
          linkedin: formData.social_links.find(l => l.platform === "LinkedIn")?.url || null,
          youtube: formData.social_links.find(l => l.platform === "YouTube")?.url || null,
          twitter: formData.social_links.find(l => l.platform === "Twitter/X")?.url || null,
          personal_website: formData.social_links.find(l => l.platform === "Website")?.url || null
        },
        contact_management: {
          contact_email: formData.contact_email,
          contact_phone: formData.contact_phone
        },
        preferences: { terms_accepted: formData.terms_accepted },
        is_draft: true
      };
      const response = await fetch(`${API_URL}/api/profiles/me`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      if (response.ok) toast.success("Draft saved");
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    await saveDraft();
    if (currentStep < WIZARD_STEPS.length) setCurrentStep(currentStep + 1);
  };

  const handleBack = () => { if (currentStep > 1) setCurrentStep(currentStep - 1); };

  // Handle AI Profile Builder output
  const handleAIApply = (aiContent) => {
    setFormData(prev => ({
      ...prev,
      about: aiContent.bio || prev.about,
      tagline: aiContent.headline || prev.tagline,
      services: aiContent.skills && aiContent.skills.length > 0 
        ? [...new Set([...prev.services, ...aiContent.skills])]
        : prev.services,
      company_type: aiContent.category || prev.company_type
    }));
    if (aiContent.bio) {
      toast.info("AI content applied! Review your company description in the Services step.");
    }
  };

  // Offering helpers
  const addOffering = () => {
    if (!newOffering.title) { toast.error("Please enter offering title"); return; }
    setFormData(prev => ({ ...prev, offerings: [...prev.offerings, { ...newOffering, id: Date.now() }] }));
    setNewOffering({ title: "", description: "", duration: "", price: "", mode: "offline" });
  };
  const removeOffering = (id) => setFormData(prev => ({ ...prev, offerings: prev.offerings.filter(o => o.id !== id) }));

  const handleSubmit = async () => {
    if (!formData.company_name || !formData.company_type) {
      toast.error("Please fill Company Name and Type");
      return;
    }
    if (!formData.terms_accepted) {
      toast.error("Please accept Terms & Conditions");
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const payload = {
        personal_info: {
          full_name: formData.company_name,
          display_name: formData.company_name.split(" ")[0],
          city: formData.city
        },
        professional_info: {
          headline: formData.tagline,
          primary_category: formData.company_type,
          bio: formData.about,
          awards: formData.awards
        },
        portfolio: { profile_photo: formData.logo },
        enterprise_info: {
          founded_year: formData.founded_year,
          team_size: formData.team_size,
          years_in_business: formData.years_in_business ? parseInt(formData.years_in_business) : null,
          services: formData.services,
          notable_clients: formData.clients,
          location: formData.location,
          branches: formData.branches,
          offerings: formData.offerings
        },
        social_presence: {
          instagram: formData.social_links.find(l => l.platform === "Instagram")?.url || null,
          facebook: formData.social_links.find(l => l.platform === "Facebook")?.url || null,
          facebook_page: formData.social_links.find(l => l.platform === "Facebook Page")?.url || null,
          linkedin: formData.social_links.find(l => l.platform === "LinkedIn")?.url || null,
          youtube: formData.social_links.find(l => l.platform === "YouTube")?.url || null,
          twitter: formData.social_links.find(l => l.platform === "Twitter/X")?.url || null,
          personal_website: formData.social_links.find(l => l.platform === "Website")?.url || null
        },
        contact_management: {
          contact_email: formData.contact_email,
          contact_phone: formData.contact_phone
        },
        preferences: { terms_accepted: formData.terms_accepted },
        is_draft: false
      };
      const response = await fetch(`${API_URL}/api/profiles/me`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      if (response.ok) {
        toast.success("Profile published!");
        navigate("/dashboard/enterprise");
      } else {
        const err = await response.json();
        throw new Error(err.detail || "Failed");
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const token = localStorage.getItem("token");
      const fd = new FormData();
      fd.append("file", file);
      fd.append("file_type", "image");
      const response = await fetch(`${API_URL}/api/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd
      });
      if (response.ok) {
        const data = await response.json();
        updateField("logo", data.url);
        toast.success("Uploaded");
      }
    } catch (error) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const progress = (currentStep / WIZARD_STEPS.length) * 100;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <Button variant="ghost" onClick={() => navigate(-1)}><ArrowLeft className="w-4 h-4 mr-2" /> Back</Button>
            <h1 className="text-lg font-semibold text-blue-600">Create Enterprise Profile</h1>
            <Button variant="outline" onClick={saveDraft} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Draft"}
            </Button>
          </div>
          <Progress value={progress} className="h-2 mb-4" />
          <div className="flex justify-between">
            {WIZARD_STEPS.map((step) => {
              const Icon = step.icon;
              const isActive = step.id === currentStep;
              const isCompleted = step.id < currentStep;
              return (
                <div key={step.id} className={`flex flex-col items-center cursor-pointer ${isActive ? "text-blue-600" : isCompleted ? "text-green-600" : "text-gray-400"}`}
                  onClick={() => setCurrentStep(step.id)}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-1 ${isActive ? "bg-blue-100" : isCompleted ? "bg-green-100" : "bg-gray-100"}`}>
                    {isCompleted ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                  </div>
                  <span className="text-xs font-medium hidden sm:block">{step.title}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <Card>
          <CardContent className="p-6">
            {/* Step 1: Company Info */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold">Company Information</h2>
                  <Button 
                    variant="outline"
                    onClick={() => setShowAIBuilder(true)}
                    className="border-blue-300 text-blue-700 hover:bg-blue-50"
                    data-testid="build-with-ai-btn"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Build with AI
                  </Button>
                </div>
                
                <ProfileEditHints 
                  onAIClick={() => setShowAIBuilder(true)}
                  onPhotoClick={() => logoInputRef.current?.click()}
                  onPublishClick={() => setCurrentStep(5)}
                />
                
                {/* Logo */}
                <div className="flex items-start gap-6">
                  <div className="space-y-2">
                    <Label>Company Logo</Label>
                    {formData.logo ? (
                      <div className="relative">
                        <img src={formData.logo} alt="Logo" className="w-24 h-24 object-cover rounded-lg border" />
                        <button onClick={() => updateField("logo", "")} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"><X className="w-3 h-3" /></button>
                      </div>
                    ) : (
                      <div className="w-24 h-24 border-2 border-dashed rounded-lg flex items-center justify-center cursor-pointer hover:border-blue-400"
                        onClick={() => logoInputRef.current?.click()}>
                        {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <Upload className="w-6 h-6 text-gray-400" />}
                      </div>
                    )}
                    <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleFileUpload(e.target.files[0])} />
                  </div>
                  
                  <div className="flex-1 space-y-4">
                    <div className="space-y-1.5">
                      <Label>Company Name <span className="text-red-500">*</span></Label>
                      <Input value={formData.company_name} onChange={(e) => updateField("company_name", e.target.value)} placeholder="Your company name" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Tagline</Label>
                      <Input value={formData.tagline} onChange={(e) => updateField("tagline", e.target.value)} placeholder="e.g., Creating Stories That Inspire" />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SearchableSelect label="Company Type" options={taxonomy.company_types} value={formData.company_type} onChange={(v) => updateField("company_type", v)}
                    placeholder="Select type" allowAdd taxonomyType="company_type" required />
                  <SearchableSelect label="City" options={taxonomy.cities} value={formData.city} onChange={(v) => updateField("city", v)}
                    placeholder="Select city" allowAdd taxonomyType="city" />
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="space-y-1.5">
                    <Label>Founded Year</Label>
                    <Input type="number" value={formData.founded_year} onChange={(e) => updateField("founded_year", e.target.value)} placeholder="e.g., 2015" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Years in Business</Label>
                    <Input type="number" value={formData.years_in_business} onChange={(e) => updateField("years_in_business", e.target.value)} placeholder="e.g., 10" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Team Size</Label>
                    <select value={formData.team_size} onChange={(e) => updateField("team_size", e.target.value)} className="w-full border rounded-lg px-3 py-2.5 text-sm">
                      <option value="">Select</option>
                      <option value="1-10">1-10</option>
                      <option value="11-50">11-50</option>
                      <option value="51-200">51-200</option>
                      <option value="201-500">201-500</option>
                      <option value="500+">500+</option>
                    </select>
                  </div>
                </div>

                {/* HQ Location */}
                <div className="space-y-1.5">
                  <Label>Headquarters Location</Label>
                  <Input value={formData.location} onChange={(e) => updateField("location", e.target.value)} 
                    placeholder="e.g., Mumbai, Maharashtra, India" />
                </div>

                {/* Branches */}
                <div className="space-y-2">
                  <Label>Branch Offices</Label>
                  <div className="flex gap-2">
                    <Input placeholder="Add branch location (press Enter)"
                      onKeyDown={(e) => { if (e.key === "Enter" && e.target.value.trim()) { updateField("branches", [...formData.branches, e.target.value.trim()]); e.target.value = ""; } }} />
                    <Button variant="outline" type="button"><Plus className="w-4 h-4" /></Button>
                  </div>
                  {formData.branches.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {formData.branches.map((b, i) => (
                        <Badge key={i} variant="secondary" className="gap-1">{b}
                          <X className="w-3 h-3 cursor-pointer" onClick={() => updateField("branches", formData.branches.filter((_, idx) => idx !== i))} />
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                {/* Contact Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label>Contact Email</Label>
                    <Input type="email" value={formData.contact_email} onChange={(e) => updateField("contact_email", e.target.value)} 
                      placeholder="business@company.com" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Contact Phone</Label>
                    <Input value={formData.contact_phone} onChange={(e) => updateField("contact_phone", e.target.value)} 
                      placeholder="+91 98765 43210" />
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Services */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <h2 className="text-xl font-semibold">About & Services</h2>
                
                <div className="space-y-1.5">
                  <Label>About Company</Label>
                  <Textarea value={formData.about} onChange={(e) => updateField("about", e.target.value)}
                    placeholder="Tell us about your company, history, mission, and what makes you unique..." rows={5} />
                </div>

                <div className="space-y-2">
                  <Label>Services Offered</Label>
                  <div className="flex gap-2">
                    <Input placeholder="Add a service (e.g., Film Production, Casting, Post Production)"
                      onKeyDown={(e) => { if (e.key === "Enter" && e.target.value.trim()) { updateField("services", [...formData.services, e.target.value.trim()]); e.target.value = ""; } }} />
                    <Button variant="outline"><Plus className="w-4 h-4" /></Button>
                  </div>
                  {formData.services.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {formData.services.map((s, i) => (
                        <Badge key={i} variant="secondary" className="gap-1">{s}
                          <X className="w-3 h-3 cursor-pointer" onClick={() => updateField("services", formData.services.filter((_, idx) => idx !== i))} />
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Notable Clients / Projects</Label>
                  <div className="flex gap-2">
                    <Input placeholder="Add notable client or project"
                      onKeyDown={(e) => { if (e.key === "Enter" && e.target.value.trim()) { updateField("clients", [...formData.clients, e.target.value.trim()]); e.target.value = ""; } }} />
                    <Button variant="outline"><Plus className="w-4 h-4" /></Button>
                  </div>
                  {formData.clients.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {formData.clients.map((c, i) => (
                        <Badge key={i} variant="outline" className="gap-1">{c}
                          <X className="w-3 h-3 cursor-pointer" onClick={() => updateField("clients", formData.clients.filter((_, idx) => idx !== i))} />
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 3: Offerings */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <h2 className="text-xl font-semibold">Packages & Classes</h2>
                <p className="text-sm text-gray-600">Add your offerings like dance classes, fitness sessions, workshops, wedding choreography packages, etc.</p>
                
                {/* Add New Offering Form */}
                <Card className="border-dashed">
                  <CardContent className="pt-4 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <Label>Title <span className="text-red-500">*</span></Label>
                        <Input value={newOffering.title} onChange={(e) => setNewOffering(p => ({ ...p, title: e.target.value }))} 
                          placeholder="e.g., Bollywood Dance Workshop" />
                      </div>
                      <div className="space-y-1.5">
                        <Label>Duration</Label>
                        <Input value={newOffering.duration} onChange={(e) => setNewOffering(p => ({ ...p, duration: e.target.value }))} 
                          placeholder="e.g., 1 hour, 4 weeks" />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Description</Label>
                      <Textarea value={newOffering.description} onChange={(e) => setNewOffering(p => ({ ...p, description: e.target.value }))} 
                        placeholder="Describe what's included in this offering..." rows={2} />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <Label>Price</Label>
                        <Input value={newOffering.price} onChange={(e) => setNewOffering(p => ({ ...p, price: e.target.value }))} 
                          placeholder="e.g., ₹2,000 / session" />
                      </div>
                      <div className="space-y-1.5">
                        <Label>Mode</Label>
                        <select value={newOffering.mode} onChange={(e) => setNewOffering(p => ({ ...p, mode: e.target.value }))} 
                          className="w-full border rounded-lg px-3 py-2.5 text-sm">
                          <option value="offline">Offline (In-person)</option>
                          <option value="online">Online</option>
                          <option value="both">Both Online & Offline</option>
                        </select>
                      </div>
                    </div>
                    <Button type="button" onClick={addOffering} className="w-full">
                      <Plus className="w-4 h-4 mr-2" /> Add Offering
                    </Button>
                  </CardContent>
                </Card>

                {/* Existing Offerings */}
                {formData.offerings.length > 0 && (
                  <div className="space-y-3">
                    <Label>Your Offerings ({formData.offerings.length})</Label>
                    {formData.offerings.map((off) => (
                      <Card key={off.id} className="relative">
                        <Button variant="ghost" size="sm" className="absolute top-2 right-2 text-gray-400 hover:text-red-500"
                          onClick={() => removeOffering(off.id)}>
                          <X className="w-4 h-4" />
                        </Button>
                        <CardContent className="pt-4">
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center justify-between pr-8">
                              <span className="font-medium">{off.title}</span>
                              <Badge variant={off.mode === "online" ? "secondary" : off.mode === "both" ? "outline" : "default"}>
                                {off.mode === "both" ? "Online & Offline" : off.mode === "online" ? "Online" : "Offline"}
                              </Badge>
                            </div>
                            {off.description && <p className="text-sm text-gray-600">{off.description}</p>}
                            <div className="flex gap-4 text-sm text-gray-500 mt-1">
                              {off.duration && <span>Duration: {off.duration}</span>}
                              {off.price && <span>Price: {off.price}</span>}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Step 4: Credentials */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <h2 className="text-xl font-semibold">Awards & Social</h2>
                
                <div className="space-y-2">
                  <Label>Awards / Recognition</Label>
                  <div className="flex gap-2">
                    <Input placeholder="Add award"
                      onKeyDown={(e) => { if (e.key === "Enter" && e.target.value.trim()) { updateField("awards", [...formData.awards, e.target.value.trim()]); e.target.value = ""; } }} />
                    <Button variant="outline"><Plus className="w-4 h-4" /></Button>
                  </div>
                  {formData.awards.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {formData.awards.map((a, i) => (
                        <Badge key={i} variant="secondary" className="gap-1">{a}
                          <X className="w-3 h-3 cursor-pointer" onClick={() => updateField("awards", formData.awards.filter((_, idx) => idx !== i))} />
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Social Links</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {["Website", "Instagram", "Facebook", "Facebook Page", "LinkedIn", "YouTube", "Twitter/X"].map((platform) => (
                      <div key={platform} className="flex items-center gap-2">
                        <span className="text-sm w-28 text-gray-600">{platform}</span>
                        <Input placeholder={`${platform} URL`}
                          value={formData.social_links.find(l => l.platform === platform)?.url || ""}
                          onChange={(e) => {
                            const existing = formData.social_links.filter(l => l.platform !== platform);
                            if (e.target.value.trim()) updateField("social_links", [...existing, { platform, url: e.target.value.trim() }]);
                            else updateField("social_links", existing);
                          }} className="flex-1" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Step 5: Review */}
            {currentStep === 5 && (
              <div className="space-y-6">
                <h2 className="text-xl font-semibold">Review & Publish</h2>
                
                <div className="bg-blue-50 rounded-lg p-4 space-y-2">
                  <h3 className="font-medium">Profile Summary</h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><span className="text-gray-500">Company:</span> {formData.company_name || "-"}</div>
                    <div><span className="text-gray-500">Type:</span> {formData.company_type || "-"}</div>
                    <div><span className="text-gray-500">City:</span> {formData.city || "-"}</div>
                    <div><span className="text-gray-500">Services:</span> {formData.services.length} added</div>
                    <div><span className="text-gray-500">Offerings:</span> {formData.offerings.length} added</div>
                    <div><span className="text-gray-500">Awards:</span> {formData.awards.length} added</div>
                    <div><span className="text-gray-500">Clients:</span> {formData.clients.length} added</div>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 border rounded-lg">
                  <input type="checkbox" checked={formData.terms_accepted} onChange={(e) => updateField("terms_accepted", e.target.checked)} className="mt-1 w-5 h-5" />
                  <div>
                    <Label>I accept the Terms of Service and Privacy Policy *</Label>
                    <p className="text-sm text-gray-500">By checking this, you agree to our terms and verify that you're authorized to represent this company.</p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between mt-6">
          <Button variant="outline" onClick={handleBack} disabled={currentStep === 1}><ArrowLeft className="w-4 h-4 mr-2" /> Previous</Button>
          {currentStep < WIZARD_STEPS.length ? (
            <Button onClick={handleNext} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Next <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={loading} className="bg-green-600 hover:bg-green-700">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />} Publish Profile
            </Button>
          )}
        </div>
      </div>

      {/* AI Profile Builder Modal */}
      <AIProfileBuilder
        isOpen={showAIBuilder}
        onClose={() => setShowAIBuilder(false)}
        profileType="enterprise"
        onApply={handleAIApply}
        existingData={{
          bio: formData.about,
          headline: formData.tagline,
          skills: formData.services
        }}
      />
    </div>
  );
}
